import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Gear, AddressBook, HouseLine, ArrowLeft, CurrencyDollar, Sun, ChartBar,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const ACCOUNT_ITEMS = [
  { to: "/settings",          label: "Settings",         icon: Gear,            testid: "account-nav-settings"  },
  { to: "/settings/digest",   label: "Morning Digest",   icon: Sun,             testid: "account-nav-digest"    },
  { to: "/settings/backfill", label: "Backfill Summary", icon: ChartBar,        testid: "account-nav-backfill"  },
  { to: "/subscriptions",  label: "Subscriptions",  icon: CurrencyDollar,  testid: "account-nav-subscriptions" },
  { to: "/contacts",       label: "Contacts",       icon: AddressBook,     testid: "account-nav-contacts"  },
  { to: "/vault",          label: "Accounts of Record", icon: AddressBook, testid: "account-nav-vault"     },
  { to: "/household",      label: "Household",      icon: HouseLine,       testid: "account-nav-household" },
];

export default function AccountLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card sticky top-0 h-screen">
        {/* Back to main app */}
        <div className="h-16 flex items-center px-5 border-b border-border">
          <button
            data-testid="account-back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={15} weight="bold" />
            Back
          </button>
        </div>

        {/* User profile card */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-sm shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="rounded-sm bg-foreground text-background text-sm font-black">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <p className="px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 mb-2">
            Account
          </p>
          {ACCOUNT_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              end
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors border-l-[3px] ${
                  isActive
                    ? "bg-primary/10 text-primary border-primary"
                    : "text-muted-foreground border-transparent hover:bg-muted/60 hover:text-foreground"
                }`
              }
            >
              <n.icon size={18} weight="bold" />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30 flex items-center px-6">
          {/* Mobile: back button */}
          <button
            className="md:hidden flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mr-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={15} weight="bold" />
          </button>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Account &amp; Settings
          </p>
        </header>

        {/* Mobile tab bar */}
        <nav className="md:hidden flex overflow-x-auto border-b border-border bg-card">
          {ACCOUNT_ITEMS.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              className={({ isActive }) =>
                `flex-1 min-w-[72px] flex flex-col items-center gap-1 py-2 text-[10px] font-bold uppercase tracking-wider ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`
              }
            >
              <n.icon size={18} weight="bold" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
